/**
 * Operator Configuration
 *
 * Defines available operators for each field type and their behavior.
 * Maps backend ConstraintType to user-friendly labels.
 */

import type { ConstraintType } from '@/types/api.types'
import type { OperatorDefinition, OperatorConfig } from './advancedFiltersTypes'

/**
 * All operator definitions with user-friendly labels.
 * Maps backend constraint types to UI display.
 */
export const OPERATOR_DEFINITIONS: Record<ConstraintType, OperatorDefinition> = {
  EQUAL: {
    value: 'EQUAL',
    label: 'equals',
    requiresValue: true,
    requiresMultipleValues: false,
    requiresRange: false,
    description: 'Exact match',
  },
  NOT_EQUAL: {
    value: 'NOT_EQUAL',
    label: 'does not equal',
    requiresValue: true,
    requiresMultipleValues: false,
    requiresRange: false,
    description: 'Excludes exact match',
  },
  CONTAINS: {
    value: 'CONTAINS',
    label: 'contains',
    requiresValue: true,
    requiresMultipleValues: false,
    requiresRange: false,
    description: 'Contains substring',
  },
  NOT_CONTAINS: {
    value: 'NOT_CONTAINS',
    label: 'does not contain',
    requiresValue: true,
    requiresMultipleValues: false,
    requiresRange: false,
    description: 'Does not contain substring',
  },
  STARTS_WITH: {
    value: 'STARTS_WITH',
    label: 'starts with',
    requiresValue: true,
    requiresMultipleValues: false,
    requiresRange: false,
    description: 'Begins with text',
  },
  ENDS_WITH: {
    value: 'ENDS_WITH',
    label: 'ends with',
    requiresValue: true,
    requiresMultipleValues: false,
    requiresRange: false,
    description: 'Ends with text',
  },
  IN: {
    value: 'IN',
    label: 'is one of',
    requiresValue: false,
    requiresMultipleValues: true,
    requiresRange: false,
    description: 'Matches any of the selected values',
  },
  NOT_IN: {
    value: 'NOT_IN',
    label: 'is not one of',
    requiresValue: false,
    requiresMultipleValues: true,
    requiresRange: false,
    description: 'Does not match any of the selected values',
  },
  IS_NULL: {
    value: 'IS_NULL',
    label: 'is empty',
    requiresValue: false,
    requiresMultipleValues: false,
    requiresRange: false,
    description: 'Field has no value',
  },
  IS_NOT_NULL: {
    value: 'IS_NOT_NULL',
    label: 'is not empty',
    requiresValue: false,
    requiresMultipleValues: false,
    requiresRange: false,
    description: 'Field has a value',
  },
  GT: {
    value: 'GT',
    label: 'greater than',
    requiresValue: true,
    requiresMultipleValues: false,
    requiresRange: false,
    description: 'Strictly greater',
  },
  GE: {
    value: 'GE',
    label: 'greater than or equal',
    requiresValue: true,
    requiresMultipleValues: false,
    requiresRange: false,
    description: 'Greater or equal',
  },
  LT: {
    value: 'LT',
    label: 'less than',
    requiresValue: true,
    requiresMultipleValues: false,
    requiresRange: false,
    description: 'Strictly less',
  },
  LE: {
    value: 'LE',
    label: 'less than or equal',
    requiresValue: true,
    requiresMultipleValues: false,
    requiresRange: false,
    description: 'Less or equal',
  },
  BETWEEN: {
    value: 'BETWEEN',
    label: 'is between',
    requiresValue: false,
    requiresMultipleValues: false,
    requiresRange: true,
    description: 'Within range (inclusive)',
  },
  // Wrapper operators - not used in UI, only for programmatic filter building
  OR: {
    value: 'OR',
    label: 'OR',
    requiresValue: false,
    requiresMultipleValues: false,
    requiresRange: false,
    description: 'Logical OR wrapper for combining filters',
  },
  AND: {
    value: 'AND',
    label: 'AND',
    requiresValue: false,
    requiresMultipleValues: false,
    requiresRange: false,
    description: 'Logical AND wrapper for combining filters',
  },
}

/**
 * Operator configurations by field type.
 * Determines which operators are available for each data type.
 */
export const OPERATOR_CONFIGS: Record<string, OperatorConfig> = {
  // String fields (name, sku, description)
  string: {
    fieldType: 'string',
    operators: [
      OPERATOR_DEFINITIONS.CONTAINS,
      OPERATOR_DEFINITIONS.NOT_CONTAINS,
      OPERATOR_DEFINITIONS.EQUAL,
      OPERATOR_DEFINITIONS.NOT_EQUAL,
      OPERATOR_DEFINITIONS.STARTS_WITH,
      OPERATOR_DEFINITIONS.ENDS_WITH,
      OPERATOR_DEFINITIONS.IN,
      OPERATOR_DEFINITIONS.NOT_IN,
      OPERATOR_DEFINITIONS.IS_NULL,
      OPERATOR_DEFINITIONS.IS_NOT_NULL,
    ],
  },

  // Numeric fields (price, cost, quantity)
  numeric: {
    fieldType: 'numeric',
    operators: [
      OPERATOR_DEFINITIONS.EQUAL,
      OPERATOR_DEFINITIONS.NOT_EQUAL,
      OPERATOR_DEFINITIONS.GT,
      OPERATOR_DEFINITIONS.GE,
      OPERATOR_DEFINITIONS.LT,
      OPERATOR_DEFINITIONS.LE,
      OPERATOR_DEFINITIONS.BETWEEN,
      OPERATOR_DEFINITIONS.IN,
      OPERATOR_DEFINITIONS.NOT_IN,
      OPERATOR_DEFINITIONS.IS_NULL,
      OPERATOR_DEFINITIONS.IS_NOT_NULL,
    ],
  },

  // UUID fields (uid, orgId, vendorId)
  uuid: {
    fieldType: 'uuid',
    operators: [
      OPERATOR_DEFINITIONS.EQUAL,
      OPERATOR_DEFINITIONS.NOT_EQUAL,
      OPERATOR_DEFINITIONS.IN,
      OPERATOR_DEFINITIONS.NOT_IN,
      OPERATOR_DEFINITIONS.IS_NULL,
      OPERATOR_DEFINITIONS.IS_NOT_NULL,
    ],
  },

  // Boolean fields (isActive, isTaxable)
  boolean: {
    fieldType: 'boolean',
    operators: [
      OPERATOR_DEFINITIONS.EQUAL,
      OPERATOR_DEFINITIONS.IS_NULL,
      OPERATOR_DEFINITIONS.IS_NOT_NULL,
    ],
  },

  // Timestamp fields (createdAt, modifiedAt)
  timestamp: {
    fieldType: 'timestamp',
    operators: [
      OPERATOR_DEFINITIONS.EQUAL,
      OPERATOR_DEFINITIONS.NOT_EQUAL,
      OPERATOR_DEFINITIONS.GT,
      OPERATOR_DEFINITIONS.GE,
      OPERATOR_DEFINITIONS.LT,
      OPERATOR_DEFINITIONS.LE,
      OPERATOR_DEFINITIONS.BETWEEN,
      OPERATOR_DEFINITIONS.IS_NULL,
      OPERATOR_DEFINITIONS.IS_NOT_NULL,
    ],
  },

  // Date fields
  date: {
    fieldType: 'date',
    operators: [
      OPERATOR_DEFINITIONS.EQUAL,
      OPERATOR_DEFINITIONS.NOT_EQUAL,
      OPERATOR_DEFINITIONS.GT,
      OPERATOR_DEFINITIONS.GE,
      OPERATOR_DEFINITIONS.LT,
      OPERATOR_DEFINITIONS.LE,
      OPERATOR_DEFINITIONS.BETWEEN,
      OPERATOR_DEFINITIONS.IS_NULL,
      OPERATOR_DEFINITIONS.IS_NOT_NULL,
    ],
  },

  // Enum fields (with allowed_values)
  enum: {
    fieldType: 'enum',
    operators: [
      OPERATOR_DEFINITIONS.EQUAL,
      OPERATOR_DEFINITIONS.NOT_EQUAL,
      OPERATOR_DEFINITIONS.IN,
      OPERATOR_DEFINITIONS.NOT_IN,
      OPERATOR_DEFINITIONS.IS_NULL,
      OPERATOR_DEFINITIONS.IS_NOT_NULL,
    ],
  },
}

/**
 * Get operators for a specific field type.
 * Falls back to string operators if type not found.
 *
 * @param fieldType - Field data type from metadata (py_type)
 * @returns Array of available operator definitions
 */
export function getOperatorsForFieldType(fieldType: string): OperatorDefinition[] {
  const config = OPERATOR_CONFIGS[fieldType] || OPERATOR_CONFIGS.string
  return config.operators
}

/**
 * Get default operator for a field type.
 * Returns the first operator in the list (most common use case).
 *
 * @param fieldType - Field data type from metadata
 * @returns Default operator definition
 */
export function getDefaultOperator(fieldType: string): OperatorDefinition {
  const operators = getOperatorsForFieldType(fieldType)
  return operators[0]
}

/**
 * Check if operator requires a value input.
 *
 * @param operator - Constraint type
 * @returns True if operator needs a value input
 */
export function operatorRequiresValue(operator: ConstraintType): boolean {
  return OPERATOR_DEFINITIONS[operator]?.requiresValue ?? false
}

/**
 * Check if operator requires multiple values (IN, NOT_IN).
 *
 * @param operator - Constraint type
 * @returns True if operator needs multiple values
 */
export function operatorRequiresMultipleValues(operator: ConstraintType): boolean {
  return OPERATOR_DEFINITIONS[operator]?.requiresMultipleValues ?? false
}

/**
 * Check if operator requires a range (BETWEEN).
 *
 * @param operator - Constraint type
 * @returns True if operator needs a range (value + value2)
 */
export function operatorRequiresRange(operator: ConstraintType): boolean {
  return OPERATOR_DEFINITIONS[operator]?.requiresRange ?? false
}
